import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { DisabledAccountRedirectService, SessionState, SignOut, SignOutOnError } from '@platform-frontend/account';
import { FetchCountries, MARKETPLACE_ERROR_HANDLERS, ProductIdentifier } from '@platform-frontend/marketplace';
import { CloseWindow, InvalidStateError, filterTrue, handleAllErrors, handleErrors, throwAndHandleError } from '@platform-frontend/util';
import { Observable, of, throwError } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { GotoAcademicRegistration } from '../academic/academic.actions';
import { FetchAssets, GotoPurchaseInformation, GotoSubscriptionManagement } from '../asset/asset.actions';
import { FetchProduct } from '../product';
import { GotoCommercialCheckout } from '../shopping-cart/shopping-cart.actions';
import {
  FetchFlowData,
  FlowCanceled,
  FlowPreparationStarted,
  FlowStarted,
  GotoFlowStart,
  PatchContractId,
  PrepareFlow,
  PublishFetchedFlowData,
  SetAcademicUserType,
  SetFlow,
  StartFlow
} from './flow.actions';
import { getStartAcademicRegistrationPreferenceFromUserType } from './get-start-academic-registration-preference-from-user-type';
import { AcademicRegistration } from './model/academic-registration';
import { AppFlow } from './model/app-flow';
import { AppFlowType } from './model/app-flow-type';
import { FlowStateModel } from './model/flow-state-model';
import { SIGN_IN_FLOW_DEFAULT } from './model/sign-in-flow';

@State<FlowStateModel>({
  name: 'flow',
  defaults: {
    flow: SIGN_IN_FLOW_DEFAULT
  }
})
@Injectable()
export class FlowState {
  constructor(private disabledAccountRedirectService: DisabledAccountRedirectService, private store: Store) {}

  isSignedIn$: Observable<boolean> = this.store.select(SessionState.isSignedIn);

  @Selector()
  static flow(state: FlowStateModel): AppFlow {
    return state.flow;
  }

  @Selector([FlowState.flow])
  static productIdentifier(state: FlowStateModel, flow: AppFlow): ProductIdentifier | null {
    if (flow.type !== 'SIGN_IN') {
      return flow.startParams.productIdentifier;
    } else if (flow.productIdentifier) {
      return flow.productIdentifier;
    }
    return null;
  }

  @Selector([FlowState.flow])
  static vendorId(state: FlowStateModel, flow: AppFlow): string | null {
    if (flow.type === 'SIGN_IN') {
      return flow.startParams.vendorId ?? null;
    }
    return null;
  }

  @Selector([FlowState.flow])
  static contractId(state: FlowStateModel, flow: AppFlow): string | null {
    if (flow.type !== 'SIGN_IN' && flow.type !== 'ACADEMIC_REGISTRATION') {
      return flow.startParams.contractId ?? null;
    }
    return null;
  }

  @Selector([FlowState.contractId])
  static isOrderToBeManaged(state: FlowStateModel, contractId: string | null): boolean {
    return contractId !== null;
  }

  @Selector([FlowState.flow])
  static metadataId(state: FlowStateModel, flow: AppFlow): string | null {
    return flow.startParams.metadataId;
  }

  @Selector([FlowState.flow])
  static locale(state: FlowStateModel, flow: AppFlow): string | null {
    return flow.startParams.locale;
  }

  @Selector()
  static flowType(state: FlowStateModel): AppFlowType {
    return state.flow.type;
  }

  @Selector([FlowState.flowType])
  static isSignInFlow(state: FlowStateModel, flowType: AppFlowType): boolean {
    return flowType === 'SIGN_IN';
  }

  @Selector()
  static isTrialFlow(state: FlowStateModel): boolean {
    return state.flow.type === 'SIGN_IN' && (state.flow.productIdentifier?.productId?.startsWith('de.yatta.umllab') ?? false);
  }

  @Action(SetFlow)
  setFlow(ctx: StateContext<FlowStateModel>, { flow }: SetFlow): void {
    ctx.patchState({ flow });
  }

  @Action(PatchContractId)
  patchContractId(ctx: StateContext<FlowStateModel>, { contractId }: PatchContractId): void {
    let flow = ctx.getState().flow;
    if (flow.type !== 'ACADEMIC_REGISTRATION' && flow.type !== 'SIGN_IN') flow = { ...flow, startParams: { ...flow.startParams, contractId } };
    ctx.patchState({ flow });
  }

  @Action(PrepareFlow)
  prepareFlow(ctx: StateContext<FlowStateModel>): Observable<unknown> {
    const flow = ctx.getState().flow;
    return ctx.dispatch(new FetchProduct(flow)).pipe(
      switchMap(() => ctx.dispatch(new FlowPreparationStarted(flow))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        INVALID_TOKEN: new SignOutOnError() // only happens for preview solutions when fetching data in-app before user is signed in, see MP-4429
      }),
      switchMap(() => this.fetchAssetsIfSignedIn(ctx))
    );
  }

  private fetchAssetsIfSignedIn(ctx: StateContext<FlowStateModel>): Observable<unknown> {
    return this.isSignedIn$.pipe(
      take(1),
      filterTrue(),
      switchMap(() => ctx.dispatch(new FetchAssets()))
    );
  }

  @Action(FlowPreparationStarted)
  fetchCountriesForFlow(ctx: StateContext<FlowStateModel>, { flow }: FlowPreparationStarted): Observable<unknown> {
    if (flow.type !== 'COMMERCIAL_CHECKOUT' && flow.type !== 'SUBSCRIPTION_MANAGEMENT' && flow.type !== 'ACADEMIC_REGISTRATION') {
      return of(null);
    } else {
      return ctx.dispatch(new FetchCountries(false));
    }
  }

  @Action(StartFlow)
  startFlow(ctx: StateContext<FlowStateModel>): Observable<void> {
    const flow = ctx.getState().flow;
    return ctx.dispatch(new FetchFlowData(flow)).pipe(
      switchMap(() => ctx.dispatch(new PublishFetchedFlowData())),
      switchMap(() => ctx.dispatch(new FlowStarted(flow))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS),
      handleAllErrors(new FlowCanceled())
    );
  }

  @Action(SignOut)
  cancelFlow(ctx: StateContext<FlowStateModel>): Observable<void> {
    return ctx.dispatch(new FlowCanceled());
  }

  @Action(SetAcademicUserType)
  setAcademicUserType(ctx: StateContext<FlowStateModel>, { userType }: SetAcademicUserType): Observable<unknown> {
    const currentFlow = ctx.getState().flow;

    if (currentFlow.type !== 'ACADEMIC_REGISTRATION') {
      return throwAndHandleError(MARKETPLACE_ERROR_HANDLERS.DEFAULT, new InvalidStateError('Not in academic checkout flow.'));
    } else {
      const flow: AcademicRegistration = {
        ...currentFlow,
        startParams: { ...currentFlow.startParams, start: getStartAcademicRegistrationPreferenceFromUserType(userType) }
      };
      return of(ctx.patchState({ flow }));
    }
  }

  @Action(GotoFlowStart)
  gotoFlowStart(ctx: StateContext<FlowStateModel>, { extras }: GotoFlowStart): Observable<unknown> {
    const flow = ctx.getState().flow;
    switch (flow.type) {
      case 'ACADEMIC_REGISTRATION':
        return ctx.dispatch(new GotoAcademicRegistration(flow.startParams, extras));
      case 'COMMERCIAL_CHECKOUT':
        return ctx.dispatch(new GotoCommercialCheckout(flow.startParams, extras));
      case 'SUBSCRIPTION_MANAGEMENT':
        return ctx.dispatch(new GotoSubscriptionManagement(flow.startParams, extras));
      case 'PURCHASE_INFORMATION':
        return ctx.dispatch(new GotoPurchaseInformation(flow.startParams, extras));
      case 'SIGN_IN':
        return ctx.dispatch(new PrepareFlow()).pipe(
          switchMap(() => this.disabledAccountRedirectService.redirectDisabledUser(false, extras?.clearNotifications)),
          filter(preventStartup => !preventStartup),
          switchMap(() => ctx.dispatch(new CloseWindow()))
        );
      default:
        return throwError(() => new InvalidStateError('Unhandled flow start navigation'));
    }
  }
}
